import { Component, OnInit } from '@angular/core';
import { MerchantService } from 'src/app/services/merchant/merchant.service';
import { AuthService } from 'src/app/services/auth/auth.service';
import { CeldaMes, DIAS_SEMANA, MESES, semanasDe } from 'src/app/utils/calendario-mes';

/**
 * FERIADOS BANCARIOS (1.6a; reunión 08-oct-2026: la pantalla la hace Mario).
 *
 * La fecha de pago al comercio es venta firme + plazo; si cae en fin de semana o feriado y el contrato
 * dice «hábil siguiente», pasa al hábil siguiente. Los feriados viven aquí, no en el código. Dar de baja
 * es baja lógica: deja de contar y queda como historia (regla de traza, 08-oct). El servidor valida
 * (FEC-07): fecha que existe, nombre, alcance NACIONAL o BANCARIO, sin repetir.
 *
 * Calendario (Mario, 08-oct: «ver a futuro todo el calendario mensual y anual»): pestañas Año · Mes · Lista,
 * abre en Año. Un clic en un día que no es feriado deja la carga lista con esa fecha; en un feriado, lo muestra
 * con su baja (en el mismo lugar, sin diálogos).
 */
@Component({
  selector: 'app-feriados',
  templateUrl: './feriados.component.html'
})
export class FeriadosComponent implements OnInit {
  anio = new Date().getFullYear();
  vista: 'anio' | 'mes' | 'lista' = 'anio';
  mesVisto = new Date().getMonth();
  readonly DIAS = DIAS_SEMANA;
  mesesDelAnio: { mes0: number; nombre: string; semanas: CeldaMes[][] }[] = [];
  semanasMes: CeldaMes[][] = [];
  diaElegido: string | null = null;
  private porDia = new Map<string, any>();
  cargando = true;
  error = '';
  aviso = '';
  feriados: any[] = [];

  nuevo = { date: '', name: '', scope: 'BANCARIO' };
  guardando = false;
  errorGuardar = '';

  /** La baja se confirma en la misma fila, en dos pasos. */
  confirmandoBaja: string | null = null;
  borrando = false;

  constructor(private merchants: MerchantService, public _auth: AuthService) { }

  // PAR-07 (Mario, 12-oct): cargar y dar de baja, solo con su permiso (hoy, la gerente de finanzas); el resto, en lectura.
  get puedeCargar(): boolean { return this._auth.hasPermission('config.feriados.create'); }
  get puedeBorrar(): boolean { return this._auth.hasPermission('config.feriados.delete'); }

  ngOnInit(): void { this.armarGrillas(); this.cargar(); }

  cargar = async (): Promise<void> => {
    this.cargando = true; this.error = '';
    try {
      this.feriados = await this.merchants.getHolidays(this.anio);
      this.porDia = new Map(this.feriados.map((f) => [f.date, f]));
    }
    catch (e: any) { this.error = e?.error?.msg || 'No se pudieron leer los feriados.'; }
    finally { this.cargando = false; this.armarGrillas(); }
  };

  cambiarAnio = (d: number): void => { this.anio += d; this.confirmandoBaja = null; this.diaElegido = null; this.cargar(); };

  /** Mes siguiente o anterior; al pasar diciembre o enero cambia de año y lee sus feriados. */
  moverMes = (d: number): void => {
    const m = this.mesVisto + d;
    this.diaElegido = null; this.confirmandoBaja = null;
    if (m < 0 || m > 11) { this.anio += m < 0 ? -1 : 1; this.mesVisto = (m + 12) % 12; this.cargar(); return; }
    this.mesVisto = m; this.armarGrillas();
  };

  verMes = (mes0: number): void => { this.mesVisto = mes0; this.vista = 'mes'; this.armarGrillas(); };

  private armarGrillas(): void {
    const hoy = this.hoy();
    this.mesesDelAnio = MESES.map((nombre, mes0) => ({ mes0, nombre, semanas: semanasDe(this.anio, mes0, hoy) }));
    this.semanasMes = semanasDe(this.anio, this.mesVisto, hoy);
  }

  get nombreMes(): string { return `${MESES[this.mesVisto]} ${this.anio}`; }

  feriadoDe = (iso: string): any => this.porDia.get(iso) || null;

  /** En un feriado: lo muestra con su baja. En otro día: deja la carga lista con esa fecha. */
  elegirDia = (iso: string): void => {
    this.diaElegido = iso; this.confirmandoBaja = null; this.aviso = ''; this.errorGuardar = '';
    if (!this.feriadoDe(iso)) {
      this.nuevo.date = iso;
      setTimeout(() => document.getElementById('frNombre')?.focus(), 0);
    }
  };

  get proximos(): any[] { const hoy = this.hoy(); return this.feriados.filter((f) => f.date >= hoy); }
  get pasados(): any[] { const hoy = this.hoy(); return this.feriados.filter((f) => f.date < hoy); }
  get porConfirmar(): number { return this.feriados.filter((f) => f.por_confirmar).length; }

  /** Lo mismo que valida el servidor, dicho antes de enviar. */
  get errores(): string[] {
    const e: string[] = [];
    if (!this.nuevo.date) e.push('Falta la fecha');
    if (!this.nuevo.name.trim()) e.push('Falta el nombre del feriado');
    if (this.nuevo.date && this.feriados.some((f) => f.date === this.nuevo.date)) e.push(`El ${this.fecha(this.nuevo.date)} ya está cargado`);
    return e;
  }

  guardar = async (): Promise<void> => {
    if (this.errores.length) return;
    this.guardando = true; this.errorGuardar = ''; this.aviso = '';
    try {
      const r = await this.merchants.createHoliday({ ...this.nuevo, name: this.nuevo.name.trim() });
      this.aviso = `Se cargó el ${this.fecha(r.date)}: ${r.name}.`;
      this.nuevo = { date: '', name: '', scope: 'BANCARIO' };
      if (+r.date.slice(0, 4) !== this.anio) this.anio = +r.date.slice(0, 4);
      await this.cargar();
    } catch (e: any) {
      this.errorGuardar = e?.error?.msg || 'No se pudo cargar el feriado.';
    } finally { this.guardando = false; }
  };

  darDeBaja = async (f: any): Promise<void> => {
    this.borrando = true; this.error = ''; this.aviso = '';
    try {
      await this.merchants.deleteHoliday(f._id);
      this.aviso = `Se dio de baja el ${this.fecha(f.date)} (${f.name}). Queda en el historial y deja de mover fechas de pago.`;
      this.confirmandoBaja = null; this.diaElegido = null;
      await this.cargar();
    } catch (e: any) {
      this.error = e?.error?.msg || 'No se pudo dar de baja.';
    } finally { this.borrando = false; }
  };

  hoy(): string {
    const d = new Date(Date.now() - 4 * 3600 * 1000); // día de Venezuela
    return d.toISOString().slice(0, 10);
  }

  fecha = (iso: string): string => {
    const d = new Date(`${iso}T12:00:00Z`);
    const dia = ['dom', 'lun', 'mar', 'mié', 'jue', 'vie', 'sáb'][d.getUTCDay()];
    return `${dia} ${iso.slice(8, 10)}/${iso.slice(5, 7)}/${iso.slice(0, 4)}`;
  };

  finDeSemana = (iso: string): boolean => [0, 6].includes(new Date(`${iso}T12:00:00Z`).getUTCDay());
}
